import { wireCopyButton } from "./copy-button.js";
import { slot } from "./dom.js";
import { wireExamples } from "./examples.js";
import { plainFlavours, writtenFlavours } from "./copy-html.js";
import { writtenNode } from "./output.js";
import type { InputScript } from "./syllable-text.js";
import { parseText, type ParsedPiece, writeParsed } from "./syllable-text.js";
import { writtenPinyin } from "./written-pinyin.js";
import { scriptFor, writerFor } from "./syllable-writers.js";
import { wireShareLink } from "./share-link.js";
import { readUrlState, rememberUrl } from "./url-state.js";

/**
 * The tools whose input is already romanised: pinyin in, and bopomofo in.
 *
 * One controller for both, because they differ in exactly one function — how a
 * word is read into syllables — and in nothing else. Everything downstream
 * takes syllables and does not care where they came from.
 *
 * Neither needs a dictionary, so neither has a loading state, an upgrade
 * button or a settings panel. They convert on the first keystroke.
 */

/**
 * Start a romanised-input tool.
 */
export function startSyllableTool(container: HTMLElement): void {
  const script = scriptFor(container.dataset["cbTool"]);
  const input = slot(container, "input");

  if (script === undefined || !(input instanceof HTMLTextAreaElement)) {
    return;
  }

  new SyllableTool(container, input, script).start();
}

class SyllableTool {
  readonly #container: HTMLElement;
  readonly #input: HTMLTextAreaElement;
  readonly #script: InputScript;
  readonly #panels: readonly HTMLElement[];

  #pieces: readonly ParsedPiece[] = [];

  constructor(
    container: HTMLElement,
    input: HTMLTextAreaElement,
    script: InputScript,
  ) {
    this.#container = container;
    this.#input = input;
    this.#script = script;
    this.#panels = [
      ...container.querySelectorAll<HTMLElement>("[data-cb-output]"),
    ];
  }

  /**
   * The pinyin panel copies its colours; every other panel copies plain text.
   *
   * Bopomofo, Wade-Giles and the rest are not tone-coloured on screen either:
   * the tone is spelled into the syllable in Gwoyeu Romatzyh, written as a
   * separate mark in bopomofo and as a digit in Wade-Giles, so colouring it as
   * well would be saying the same thing twice.
   */
  #wireCopies(): void {
    for (const panel of this.#panels) {
      const button = slot(panel, "copy");
      const output = slot(panel, "output");

      if (!(button instanceof HTMLButtonElement) || output === undefined) {
        continue;
      }

      const id = panel.dataset["cbOutput"];

      wireCopyButton(button, () => {
        const written = output.textContent;
        if (written === "") {
          return undefined;
        }

        return id === "pinyin"
          ? writtenFlavours(writtenPinyin(this.#pieces), this.#container)
          : plainFlavours(written);
      });
    }
  }

  #convert(): void {
    this.#pieces = parseText(this.#input.value, this.#script);
    rememberUrl({ text: this.#input.value });

    for (const panel of this.#panels) {
      const writer = writerFor(panel.dataset["cbOutput"]);
      const output = slot(panel, "output");

      if (writer === undefined || output === undefined) {
        continue;
      }

      if (panel.dataset["cbOutput"] === "pinyin") {
        output.replaceChildren(
          ...writtenPinyin(this.#pieces).map((written) => writtenNode(written)),
        );
      } else {
        output.textContent = writeParsed(
          this.#pieces,
          writer.write,
          writer.hasCapitals,
        );
      }
    }
  }

  start(): void {
    const fromUrl = readUrlState(location.search);
    if (fromUrl.text !== undefined) {
      this.#input.value = fromUrl.text;
    }

    this.#input.addEventListener("input", () => {
      this.#convert();
    });

    wireExamples(this.#container, (text) => {
      this.#input.value = text;
      this.#convert();
    });

    this.#wireCopies();

    wireShareLink(this.#container, () => ({ text: this.#input.value }));

    this.#convert();
  }
}
